import { useEffect, useRef } from "react";

// Reusable thumbnail strip with hover-to-preview + click-to-commit.
// Currently powers the Vial Kit PDP gallery and the Reconstitution Kit
// bundle gallery. The 120ms debounce sits inside the ask'd 100–150ms
// window, long enough that dragging the cursor across the strip on the
// way somewhere else doesn't churn through every angle, short enough
// that intent (a real pause on a thumb) feels instant. Click bypasses
// the debounce entirely (touch/mobile + power-user shortcut).
const HOVER_PREVIEW_DELAY_MS = 120;

export default function HoverPreviewThumbs({ images, activeIdx, onSelect, altPrefix, testIdPrefix }) {
  const hoverTimerRef = useRef(null);

  // Any pending swap dies with the component so leaving the PDP
  // mid-hover doesn't fire on the next mount.
  useEffect(
    () => () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    },
    []
  );

  const clearPending = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const previewOnHover = (i) => {
    // Restart the timer from scratch on every enter so a quick sweep
    // across several thumbs doesn't queue up a burst of swaps.
    clearPending();
    hoverTimerRef.current = setTimeout(() => {
      onSelect(i);
      hoverTimerRef.current = null;
    }, HOVER_PREVIEW_DELAY_MS);
  };

  const commitOnClick = (i) => {
    clearPending();
    onSelect(i);
  };

  return (
    <div className="mt-4 flex gap-2" data-testid={`${testIdPrefix}-thumbs`}>
      {images.map((src, i) => (
        <button
          key={src}
          type="button"
          onClick={() => commitOnClick(i)}
          onMouseEnter={() => previewOnHover(i)}
          onMouseLeave={clearPending}
          onFocus={() => previewOnHover(i)}
          onBlur={clearPending}
          className={`relative aspect-square w-16 shrink-0 overflow-hidden rounded-md bg-white transition-colors sm:w-20 ${
            activeIdx === i ? "border-2 border-navy" : "border border-line hover:border-navy/50"
          }`}
          aria-label={`View angle ${i + 1}`}
          aria-current={activeIdx === i}
          data-testid={`${testIdPrefix}-thumb-${i}`}
        >
          <img
            src={src}
            alt={`${altPrefix}, thumbnail ${i + 1}`}
            className="absolute inset-0 h-full w-full object-contain object-center p-1.5"
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        </button>
      ))}
    </div>
  );
}
